import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  GripVertical,
  FileText,
  Building,
  User,
  Type,
  Table,
  Calendar,
  PenTool,
  Minus,
  ArrowDown,
  Eye,
  Save,
  X,
  ChevronLeft,
  Settings,
  Layout,
  Upload,
  Image,
  Download,
  Columns,
  Search,
  ChevronDown,
  ChevronRight,
  Briefcase,
  MapPin,
  Clock,
  Sun,
  Bell,
  CreditCard,
  ListOrdered,
  RotateCcw,
  RotateCw,
  AlertTriangle,
  Check,
  Layers,
  PanelRightOpen,
  PanelRightClose
} from 'lucide-react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Block presets for quick insertion
const BLOCK_PRESETS = {
  'employee_section': {
    name: 'Employee Section',
    description: 'Employee info + job position + work location',
    blocks: [
      { block_type: 'employee_info', settings: { width: '100', position: 'left', newRow: true } },
      { block_type: 'job_position', settings: { width: '50', position: 'left', newRow: true } },
      { block_type: 'work_location', settings: { width: '50', position: 'right', newRow: false } },
    ]
  },
  'b2b_section': {
    name: 'B2B Section',
    description: 'Contractor info + rate + notice period',
    blocks: [
      { block_type: 'contractor_info', settings: { width: '100', position: 'left', newRow: true } },
      { block_type: 'rate_table', settings: { width: '50', position: 'left', newRow: true } },
      { block_type: 'notice_period', settings: { width: '50', position: 'right', newRow: false } },
    ]
  },
  'employment_details': {
    name: 'Employment Terms',
    description: 'Working hours + vacation + notice period',
    blocks: [
      { block_type: 'working_hours', settings: { width: '33', position: 'left', newRow: true } },
      { block_type: 'vacation_days', settings: { width: '33', position: 'center', newRow: false } },
      { block_type: 'notice_period', settings: { width: '33', position: 'right', newRow: false } },
    ]
  },
  'payment_section': {
    name: 'Payment Section',
    description: 'Rate + bank details',
    blocks: [
      { block_type: 'rate_table', settings: { width: '50', position: 'left', newRow: true } },
      { block_type: 'bank_details', settings: { width: '50', position: 'right', newRow: false } },
    ]
  },
  'signature_section': {
    name: 'Signature Section',
    description: 'Date and place + signature lines',
    blocks: [
      { block_type: 'date_place', settings: { width: '100', position: 'left', newRow: true } },
      { block_type: 'spacer', settings: { width: '100', position: 'left', newRow: true, height: 10 } },
      { block_type: 'signature_lines', settings: { width: '100', position: 'left', newRow: true } },
    ]
  },
};

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['clean']
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet', 'align'
];

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

// Block type icons
const BLOCK_ICONS = {
  logo: Layout,
  header: Type,
  company_info: Building,
  employee_info: User,
  contractor_info: Building,
  rich_text: FileText,
  bilingual_text: Columns,
  rate_table: Table,
  duration: Calendar,
  date_place: Calendar,
  job_position: Briefcase,
  work_location: MapPin,
  working_hours: Clock,
  vacation_days: Sun,
  notice_period: Bell,
  bank_details: CreditCard,
  numbered_list: ListOrdered,
  custom_table: Table,
  two_columns: Columns,
  signatures: PenTool,
  signature_lines: PenTool,
  divider: Minus,
  spacer: ArrowDown,
  page_break: FileText,
};

const ContractTemplateBuilder = () => {
  const { user } = useContext(UserContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [blockTypes, setBlockTypes] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [variables, setVariables] = useState({});
  const [showNewModal, setShowNewModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Builder state
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [variableSearch, setVariableSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  // New UX features state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [livePreviewUrl, setLivePreviewUrl] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [copiedVariable, setCopiedVariable] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [activeFieldType, setActiveFieldType] = useState('content_pl'); // 'content_pl' or 'content_en'

  // Refs
  const activeTextFieldRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const isUndoRedoRef = useRef(false);

  // Resizable preview panel
  const [previewPanelWidth, setPreviewPanelWidth] = useState(384); // default w-96 = 384px
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // Handle preview panel resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const containerRect = resizeRef.current?.parentElement?.getBoundingClientRect();
      if (!containerRect) return;

      const newWidth = containerRect.right - e.clientX;
      // Min 300px, max 800px
      setPreviewPanelWidth(Math.min(800, Math.max(300, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (user) {
      fetchTemplates();
      fetchMetadata();
    }
  }, [user]);

  // Track blocks changes for undo/redo and auto-save
  useEffect(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    if (blocks.length > 0 || history.length > 0) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(JSON.stringify(blocks));
      if (newHistory.length > 50) newHistory.shift(); // Limit history
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setHasUnsavedChanges(true);
    }
  }, [blocks]);

  // Auto-save after 30 seconds of inactivity
  useEffect(() => {
    if (hasUnsavedChanges && selectedTemplate && editMode) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, blocks]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!editMode) return;

      // Ctrl+S - Save
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSaveBlocks();
      }
      // Ctrl+Z - Undo
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y or Ctrl+Shift+Z - Redo
      if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        handleRedo();
      }
      // Ctrl+D - Duplicate selected block
      if (e.ctrlKey && e.key === 'd' && selectedBlock) {
        e.preventDefault();
        duplicateBlock(selectedBlock);
      }
      // Delete - Delete selected block
      if (e.key === 'Delete' && selectedBlock && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        deleteBlock(selectedBlock);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, selectedBlock, historyIndex, history]);

  // Validate blocks
  useEffect(() => {
    if (!editMode || blocks.length === 0) {
      setValidationWarnings([]);
      return;
    }

    const warnings = [];

    // Check for empty rich_text blocks
    blocks.forEach((block, index) => {
      if (block.block_type === 'rich_text' && !block.content_pl?.trim()) {
        warnings.push({ blockId: block.id, message: `Blok ${index + 1} (Rich Text) jest pusty` });
      }
      if (block.block_type === 'header' && !block.content_pl?.trim()) {
        warnings.push({ blockId: block.id, message: `Blok ${index + 1} (Nagłówek) jest pusty` });
      }
    });

    // Check for missing required blocks
    const hasSignature = blocks.some(b => b.block_type === 'signatures' || b.block_type === 'signature_lines');
    if (!hasSignature && blocks.length > 3) {
      warnings.push({ message: 'Brak bloku podpisów w szablonie' });
    }

    setValidationWarnings(warnings);
  }, [blocks, editMode]);

  // Update live preview
  useEffect(() => {
    if (showLivePreview && selectedTemplate && hasUnsavedChanges) {
      const timer = setTimeout(() => {
        updateLivePreview();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [blocks, showLivePreview]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setBlocks(JSON.parse(history[newIndex]));
    }
  }, [history, historyIndex]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setBlocks(JSON.parse(history[newIndex]));
    }
  }, [history, historyIndex]);

  // Auto-save function
  const handleAutoSave = async () => {
    if (!selectedTemplate || !hasUnsavedChanges) return;

    try {
      await axios.post(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/update_blocks/`,
        { blocks },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  // Duplicate block function
  const duplicateBlock = (blockId) => {
    const blockToDuplicate = blocks.find(b => b.id === blockId);
    if (!blockToDuplicate) return;

    const newBlock = {
      ...JSON.parse(JSON.stringify(blockToDuplicate)),
      id: `new-${Date.now()}`,
      order: blocks.length
    };

    const blockIndex = blocks.findIndex(b => b.id === blockId);
    const newBlocks = [...blocks];
    newBlocks.splice(blockIndex + 1, 0, newBlock);
    setBlocks(newBlocks.map((block, index) => ({ ...block, order: index })));
    setSelectedBlock(newBlock.id);
  };

  // Add preset blocks
  const addPreset = (presetKey) => {
    const preset = BLOCK_PRESETS[presetKey];
    if (!preset) return;

    const newBlocks = preset.blocks.map((blockConfig, index) => ({
      id: `new-${Date.now()}-${index}`,
      block_type: blockConfig.block_type,
      order: blocks.length + index,
      content_pl: '',
      content_en: '',
      alignment: 'left',
      font_size: 10,
      is_bold: false,
      margin_top: 0,
      margin_bottom: 5,
      settings: blockConfig.settings,
      is_visible: true
    }));

    setBlocks([...blocks, ...newBlocks]);
    setShowPresets(false);
  };

  // Insert variable into active block
  const insertVariable = (variable) => {
    // If there's an active block, append variable to its content
    if (activeBlockId) {
      const block = blocks.find(b => b.id === activeBlockId);
      if (block) {
        const currentContent = block[activeFieldType] || '';
        // For rich text, append at the end (before closing tag if exists)
        let newContent;
        if (currentContent.includes('</p>')) {
          // Insert before last </p>
          const lastPIndex = currentContent.lastIndexOf('</p>');
          newContent = currentContent.substring(0, lastPIndex) + variable + currentContent.substring(lastPIndex);
        } else if (currentContent) {
          newContent = currentContent + variable;
        } else {
          newContent = `<p>${variable}</p>`;
        }
        updateBlock(activeBlockId, { [activeFieldType]: newContent });
        setCopiedVariable(variable);
        setTimeout(() => setCopiedVariable(null), 1500);
        return;
      }
    }

    // Fallback: copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(variable);
    }
    setCopiedVariable(variable);
    setTimeout(() => setCopiedVariable(null), 2000);
  };

  // Load preview without saving (for initial load)
  const loadPreview = async (templateId) => {
    if (!templateId) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/contract-templates/${templateId}/preview/`,
        {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          responseType: 'blob'
        }
      );

      // Check if response is actually a PDF or an error
      if (response.data.type === 'application/json' || response.status !== 200) {
        const text = await response.data.text();
        console.error('Preview returned error:', text);
        try {
          const errorData = JSON.parse(text);
          console.error('Error details:', errorData);
          alert(`Preview error: ${errorData.error || 'Unknown error'}`);
        } catch (e) {
          console.error('Raw error response:', text);
        }
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (livePreviewUrl) {
        window.URL.revokeObjectURL(livePreviewUrl);
      }

      setLivePreviewUrl(url);
    } catch (error) {
      console.error('Error loading preview:', error);
      // Try to read error response if it's a blob
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          console.error('Server error response:', text);
          try {
            const errorData = JSON.parse(text);
            console.error('Parsed error:', errorData);
            alert(`Preview failed: ${errorData.error || 'Unknown error'}\n\nCheck console for details.`);
          } catch (e) {
            console.error('Raw error text:', text);
          }
        } catch (e) {
          console.error('Could not read error blob:', e);
        }
      }
    }
  };

  // Update live preview (save blocks first, then load preview)
  const updateLivePreview = async () => {
    if (!selectedTemplate?.id) return;

    try {
      // First save current blocks
      await axios.post(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/update_blocks/`,
        { blocks },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );

      // Then get preview
      const response = await axios.get(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/preview/`,
        {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (livePreviewUrl) {
        window.URL.revokeObjectURL(livePreviewUrl);
      }

      setLivePreviewUrl(url);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error updating live preview:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contract-templates/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [typesRes, blockTypesRes, varsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/contract-templates/template_types/`, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }),
        axios.get(`${API_BASE_URL}/contract-templates/block_types/`, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        }),
        axios.get(`${API_BASE_URL}/contract-templates/variables/`, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        })
      ]);
      setTemplateTypes(typesRes.data);
      setBlockTypes(blockTypesRes.data);
      setVariables(varsRes.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      // Clear old preview immediately when switching templates
      if (livePreviewUrl) {
        window.URL.revokeObjectURL(livePreviewUrl);
        setLivePreviewUrl(null);
      }

      const response = await axios.get(`${API_BASE_URL}/contract-templates/${template.id}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setSelectedTemplate(response.data);
      setBlocks(response.data.blocks || []);
      setEditMode(true);
      // Auto-load preview when template is selected
      loadPreview(response.data.id);
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const handleCreateTemplate = async (data) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/contract-templates/`, data, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setSelectedTemplate(response.data);
      setBlocks([]);
      setEditMode(true);
      setShowNewModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error creating template');
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await axios.post(`${API_BASE_URL}/contract-templates/${templateId}/duplicate/`, {}, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/contract-templates/${templateId}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchTemplates();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleSaveBlocks = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/update_blocks/`,
        { blocks },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving blocks:', error);
      alert('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplate?.id) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/preview/`,
        {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Clean up previous URL
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(url);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Error generating preview. Make sure template is saved first.');
    }
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedTemplate?.id) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setSelectedTemplate(response.data);
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo');
    }
  };

  const addBlock = (blockType) => {
    const newBlock = {
      id: `new-${Date.now()}`,
      block_type: blockType,
      order: blocks.length,
      content_pl: '',
      content_en: '',
      alignment: 'left',
      font_size: 10,
      is_bold: false,
      margin_top: 0,
      margin_bottom: 5,
      settings: {
        width: '100',
        position: 'left',
        newRow: true
      },
      is_visible: true
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlock(newBlock.id);
  };

  const updateBlock = (blockId, updates) => {
    setBlocks(blocks.map(block =>
      block.id === blockId ? { ...block, ...updates } : block
    ));
  };

  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(block => block.id !== blockId));
    if (selectedBlock === blockId) {
      setSelectedBlock(null);
    }
  };

  const moveBlock = (fromIndex, toIndex) => {
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, moved);
    setBlocks(newBlocks.map((block, index) => ({ ...block, order: index })));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedBlock(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedBlock === null || draggedBlock === index) return;
    moveBlock(draggedBlock, index);
    setDraggedBlock(index);
  };

  const handleDragEnd = () => {
    setDraggedBlock(null);
  };

  // Template list view
  if (!editMode) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contract Templates</h1>
            <p className="text-sm text-gray-600">Create and manage contract templates for your company</p>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-4">Create your first contract template to get started</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-500">{template.template_type_display}</p>
                    </div>
                    {template.is_default && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Default</span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                  )}
                  <div className="text-xs text-gray-400 mb-3">
                    {template.block_count} blocks • Updated {new Date(template.updated_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSelectTemplate(template)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicateTemplate(template.id)}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      title="Duplicate"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Template Modal */}
        {showNewModal && (
          <NewTemplateModal
            templateTypes={templateTypes}
            onClose={() => setShowNewModal(false)}
            onCreate={handleCreateTemplate}
          />
        )}
      </div>
    );
  }

  // Builder view
  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setEditMode(false);
              setSelectedTemplate(null);
            }}
            className="p-1.5 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="font-semibold text-gray-900">{selectedTemplate?.name}</h2>
            <p className="text-xs text-gray-500">{selectedTemplate?.template_type_display}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className="flex items-center border-r pr-2 mr-2">
            <button
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>

          {/* Save Status */}
          <div className="flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border-r pr-3 mr-2">
            {hasUnsavedChanges ? (
              <>
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                <span className="text-yellow-600">Unsaved</span>
              </>
            ) : lastSaved ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-green-600">Saved {lastSaved.toLocaleTimeString()}</span>
              </>
            ) : (
              <span className="text-gray-400">No changes</span>
            )}
          </div>

          {/* Validation warnings */}
          {validationWarnings.length > 0 && (
            <div className="relative group">
              <button className="p-1.5 hover:bg-yellow-50 rounded text-yellow-600">
                <AlertTriangle className="w-4 h-4" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-64 bg-white border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="p-2 text-xs">
                  <div className="font-medium text-yellow-700 mb-1">Warnings ({validationWarnings.length})</div>
                  {validationWarnings.map((w, i) => (
                    <div key={i} className="text-gray-600 py-0.5">{w.message}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Template Type selector */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-500">Type:</span>
            <select
              value={selectedTemplate?.template_type || 'employment'}
              onChange={async (e) => {
                try {
                  const response = await axios.patch(
                    `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/`,
                    { template_type: e.target.value },
                    { headers: { Authorization: `Bearer ${user.accessToken}` } }
                  );
                  setSelectedTemplate(response.data);
                  // Also update in templates list
                  setTemplates(templates.map(t =>
                    t.id === selectedTemplate.id ? response.data : t
                  ));
                } catch (error) {
                  console.error('Error updating template type:', error);
                }
              }}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {templateTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Language options */}
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-500">Languages:</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTemplate?.include_polish !== false}
                onChange={async (e) => {
                  try {
                    const response = await axios.patch(
                      `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/`,
                      { include_polish: e.target.checked },
                      { headers: { Authorization: `Bearer ${user.accessToken}` } }
                    );
                    setSelectedTemplate(response.data);
                  } catch (error) {
                    console.error('Error updating template:', error);
                  }
                }}
                className="rounded text-blue-600"
              />
              <span className="text-sm text-gray-700">PL</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTemplate?.include_english !== false}
                onChange={async (e) => {
                  try {
                    const response = await axios.patch(
                      `${API_BASE_URL}/contract-templates/${selectedTemplate.id}/`,
                      { include_english: e.target.checked },
                      { headers: { Authorization: `Bearer ${user.accessToken}` } }
                    );
                    setSelectedTemplate(response.data);
                  } catch (error) {
                    console.error('Error updating template:', error);
                  }
                }}
                className="rounded text-blue-600"
              />
              <span className="text-sm text-gray-700">EN</span>
            </label>
          </div>

          {/* Preview Button - toggles live preview panel */}
          <button
            onClick={() => {
              setShowLivePreview(!showLivePreview);
              // Load preview without saving when opening panel
              if (!showLivePreview && selectedTemplate?.id) {
                loadPreview(selectedTemplate.id);
              }
            }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${
              showLivePreview
                ? 'bg-blue-100 text-blue-600 border border-blue-200'
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSaveBlocks}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Builder Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Block Palette */}
        <div className="w-64 bg-white border-r overflow-y-auto p-4">
          {/* Presets */}
          <div className="mb-4">
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
            >
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Quick Presets
              </span>
              <ChevronDown className={`w-4 h-4 transform transition-transform ${showPresets ? 'rotate-180' : ''}`} />
            </button>
            {showPresets && (
              <div className="mt-2 space-y-1">
                {Object.entries(BLOCK_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => addPreset(key)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded border border-gray-200"
                  >
                    <div className="font-medium text-gray-900">{preset.name}</div>
                    <div className="text-gray-500">{preset.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <h3 className="font-medium text-gray-900 mb-3">Add Blocks</h3>
          <div className="space-y-2">
            {blockTypes.map(blockType => {
              const Icon = BLOCK_ICONS[blockType.value] || FileText;
              return (
                <button
                  key={blockType.value}
                  onClick={() => addBlock(blockType.value)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 rounded border border-gray-200"
                >
                  <Icon className="w-4 h-4 text-gray-400" />
                  {blockType.label}
                </button>
              );
            })}
          </div>

          {/* Variables */}
          <h3 className="font-medium text-gray-900 mt-6 mb-2">Variables</h3>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search variables..."
              value={variableSearch}
              onChange={(e) => setVariableSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Active block indicator */}
          <div className={`mb-3 p-2 rounded text-xs ${activeBlockId ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
            {activeBlockId ? (
              <>Click variable to insert into active block</>
            ) : (
              <>Click on a text field first to insert variables</>
            )}
          </div>

          <div className="space-y-2 text-xs">
            {Object.entries(variables).map(([category, vars]) => {
              // Filter variables by search
              const filteredVars = vars.filter(([variable, description]) =>
                variableSearch === '' ||
                variable.toLowerCase().includes(variableSearch.toLowerCase()) ||
                description.toLowerCase().includes(variableSearch.toLowerCase())
              );

              if (filteredVars.length === 0) return null;

              const isExpanded = expandedCategories[category] === true;

              // Sample preview values
              const previewValues = {
                '{{company_name}}': 'Sample Company',
                '{{company_address}}': 'ul. Przykładowa 1',
                '{{company_tax_id}}': '1234567890',
                '{{employee_name}}': 'Jan Kowalski',
                '{{employee_email}}': 'jan@email.com',
                '{{contract_number}}': 'UOP/2024/001',
                '{{creation_date}}': '01.01.2024',
                '{{start_date}}': '01.01.2024',
                '{{end_date}}': '31.12.2024',
                '{{rate}}': '100.00',
                '{{rate_with_currency}}': '100.00 €',
                '{{rate_in_words_pl}}': 'sto euro',
                '{{rate_in_words_en}}': 'one hundred euros',
                '{{rate_with_words_pl}}': '100.00 € (sto euro)',
                '{{rate_with_words_en}}': '100.00 € (one hundred euros)',
                '{{rate_type_pl}}': 'godzinowa',
                '{{rate_type_en}}': 'Hourly',
                '{{signature_date}}': '01.01.2024',
              };

              return (
                <div key={category} className="border rounded-md overflow-hidden">
                  <button
                    onClick={() => setExpandedCategories(prev => ({
                      ...prev,
                      [category]: !isExpanded
                    }))}
                    className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-left"
                  >
                    <span className="font-medium text-gray-700 capitalize">{category}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400">{filteredVars.length}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-1 space-y-1">
                      {filteredVars.map(([variable, description]) => (
                        <div
                          key={variable}
                          className={`group px-2 py-1.5 rounded cursor-pointer transition-colors ${
                            copiedVariable === variable ? 'bg-green-100' : 'bg-gray-50 hover:bg-blue-50'
                          }`}
                          onClick={() => insertVariable(variable)}
                          title={`${description} (Click to copy)`}
                        >
                          <div className="flex items-center justify-between">
                            <code className="text-blue-600 font-mono">
                              {copiedVariable === variable ? '✓ Copied!' : variable}
                            </code>
                            {copiedVariable === variable ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-300 group-hover:text-gray-500" />
                            )}
                          </div>
                          {previewValues[variable] && (
                            <p className="text-gray-400 mt-0.5 truncate">
                              → {previewValues[variable]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Click to copy variable
          </p>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow min-h-[800px] p-8">
            {blocks.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Layout className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Drag blocks here to build your template</p>
              </div>
            ) : (
              <div className="space-y-1">
                {(() => {
                  // Group blocks into rows
                  const rows = [];
                  let currentRow = [];

                  blocks.forEach((block, index) => {
                    // First block always starts new row
                    const isNewRow = index === 0 || block.settings?.newRow !== false;

                    if (isNewRow && currentRow.length > 0) {
                      rows.push(currentRow);
                      currentRow = [];
                    }
                    currentRow.push({ block, index });
                  });

                  if (currentRow.length > 0) {
                    rows.push(currentRow);
                  }

                  return (
                    <>
                      {rows.map((row, rowIndex) => {
                        // Get the index of the first block in this row for drop zone
                        const firstBlockIndex = row[0].index;
                        // Get the index after the last block in this row
                        const lastBlockIndex = row[row.length - 1].index;

                        return (
                          <React.Fragment key={rowIndex}>
                            {/* Drop zone before row */}
                            <div
                              className={`h-2 rounded transition-all ${
                                draggedBlock !== null ? 'bg-blue-100 hover:bg-blue-200 hover:h-6' : ''
                              }`}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (draggedBlock !== null && draggedBlock !== firstBlockIndex && draggedBlock !== firstBlockIndex - 1) {
                                  e.currentTarget.classList.add('h-6', 'bg-blue-300');
                                }
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.classList.remove('h-6', 'bg-blue-300');
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('h-6', 'bg-blue-300');
                                if (draggedBlock !== null && draggedBlock !== firstBlockIndex) {
                                  moveBlock(draggedBlock, firstBlockIndex);
                                  setDraggedBlock(null);
                                }
                              }}
                            />

                            {/* Row content */}
                            <div className="flex gap-2">
                              {row.map(({ block, index }) => {
                                const Icon = BLOCK_ICONS[block.block_type] || FileText;
                                const width = block.settings?.width || '100';

                                return (
                                  <div
                                    key={block.id}
                                    style={{ width: row.length > 1 ? `${width}%` : '100%' }}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => setSelectedBlock(block.id)}
                                    className={`group relative border rounded p-3 cursor-pointer transition-colors flex-shrink-0 ${
                                      selectedBlock === block.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    } ${draggedBlock === index ? 'opacity-50' : ''}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <GripVertical className="w-4 h-4 text-gray-300 cursor-move" />
                                      <Icon className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm font-medium text-gray-700 truncate">
                                        {blockTypes.find(t => t.value === block.block_type)?.label || block.block_type}
                                      </span>
                                      {width !== '100' && (
                                        <span className="text-xs text-gray-400">{width}%</span>
                                      )}
                                      <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            duplicateBlock(block.id);
                                          }}
                                          className="p-1 text-gray-400 hover:text-blue-500"
                                          title="Duplicate (Ctrl+D)"
                                        >
                                          <Copy className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteBlock(block.id);
                                          }}
                                          className="p-1 text-gray-400 hover:text-red-500"
                                          title="Delete (Del)"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                    {block.content_pl && (
                                      <p
                                        className={`mt-2 text-sm text-gray-500 truncate pl-6 ${
                                          block.alignment === 'center' ? 'text-center'
                                          : block.alignment === 'right' ? 'text-right'
                                          : ''
                                        }`}
                                      >
                                        {block.content_pl.replace(/<[^>]*>/g, '').substring(0, 50)}
                                        {block.content_pl.length > 50 ? '...' : ''}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </React.Fragment>
                        );
                      })}

                      {/* Final drop zone after all rows */}
                      <div
                        className={`h-2 rounded transition-all ${
                          draggedBlock !== null ? 'bg-blue-100 hover:bg-blue-200 hover:h-6' : ''
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (draggedBlock !== null && draggedBlock !== blocks.length - 1) {
                            e.currentTarget.classList.add('h-6', 'bg-blue-300');
                          }
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('h-6', 'bg-blue-300');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('h-6', 'bg-blue-300');
                          if (draggedBlock !== null) {
                            moveBlock(draggedBlock, blocks.length - 1);
                            setDraggedBlock(null);
                          }
                        }}
                      />
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* English Page Preview */}
          {selectedTemplate?.include_english && blocks.length > 0 && (
            <div className="max-w-3xl mx-auto mt-8">
              <div className="text-center mb-2">
                <span className="text-xs font-medium text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                  Page 2 - English Version
                </span>
              </div>
              <div className="bg-white rounded-lg shadow min-h-[400px] p-8 opacity-75">
                <div className="space-y-2">
                  {blocks.map((block) => {
                    const Icon = BLOCK_ICONS[block.block_type] || FileText;
                    return (
                      <div
                        key={`en-${block.id}`}
                        className="border border-gray-200 rounded p-3 bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-600">
                            {blockTypes.find(t => t.value === block.block_type)?.label || block.block_type}
                          </span>
                        </div>
                        {block.content_en && (
                          <p className="mt-2 text-sm text-gray-500 truncate pl-6">
                            {block.content_en.replace(/<[^>]*>/g, '').substring(0, 50)}
                            {block.content_en.length > 50 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Block Editor Panel */}
        {selectedBlock && (
          <div className="w-80 bg-white border-l overflow-y-auto p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-gray-900">Block Settings</h3>
              <button
                onClick={() => setSelectedBlock(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {(() => {
              const block = blocks.find(b => b.id === selectedBlock);
              if (!block) return null;

              return (
                <div className="space-y-4">
                  {/* Content for rich_text blocks */}
                  {block.block_type === 'rich_text' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content (Polish)
                        </label>
                        <div
                          className={`rich-text-editor ${activeBlockId === block.id && activeFieldType === 'content_pl' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => { setActiveBlockId(block.id); setActiveFieldType('content_pl'); }}
                        >
                          <ReactQuill
                            theme="snow"
                            value={block.content_pl}
                            onChange={(value) => updateBlock(block.id, { content_pl: value })}
                            onFocus={() => { setActiveBlockId(block.id); setActiveFieldType('content_pl'); }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter Polish text..."
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content (English)
                        </label>
                        <div
                          className={`rich-text-editor ${activeBlockId === block.id && activeFieldType === 'content_en' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => { setActiveBlockId(block.id); setActiveFieldType('content_en'); }}
                        >
                          <ReactQuill
                            theme="snow"
                            value={block.content_en}
                            onChange={(value) => updateBlock(block.id, { content_en: value })}
                            onFocus={() => { setActiveBlockId(block.id); setActiveFieldType('content_en'); }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter English text..."
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Bilingual text block - side by side columns */}
                  {block.block_type === 'bilingual_text' && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                        <p className="text-xs text-blue-700">
                          This block displays Polish and English text side by side in two columns.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Left Column (Polish)
                        </label>
                        <div
                          className={`rich-text-editor ${activeBlockId === block.id && activeFieldType === 'content_pl' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => { setActiveBlockId(block.id); setActiveFieldType('content_pl'); }}
                        >
                          <ReactQuill
                            theme="snow"
                            value={block.content_pl}
                            onChange={(value) => updateBlock(block.id, { content_pl: value })}
                            onFocus={() => { setActiveBlockId(block.id); setActiveFieldType('content_pl'); }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter Polish text..."
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Right Column (English)
                        </label>
                        <div
                          className={`rich-text-editor ${activeBlockId === block.id && activeFieldType === 'content_en' ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => { setActiveBlockId(block.id); setActiveFieldType('content_en'); }}
                        >
                          <ReactQuill
                            theme="snow"
                            value={block.content_en}
                            onChange={(value) => updateBlock(block.id, { content_en: value })}
                            onFocus={() => { setActiveBlockId(block.id); setActiveFieldType('content_en'); }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter English text..."
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Logo block */}
                  {block.block_type === 'logo' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Logo Preview
                      </label>
                      {selectedTemplate?.logo ? (
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <img
                            src={selectedTemplate.logo}
                            alt="Company Logo"
                            className="max-h-32 mx-auto object-contain"
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Image className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No logo uploaded</p>
                        </div>
                      )}
                      <label className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer">
                        <Upload className="w-4 h-4" />
                        {selectedTemplate?.logo ? 'Change Logo' : 'Upload Logo'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}

                  {/* Header block */}
                  {block.block_type === 'header' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title (Polish)
                        </label>
                        <input
                          type="text"
                          value={block.content_pl}
                          onChange={(e) => updateBlock(block.id, { content_pl: e.target.value })}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                          placeholder="UMOWA"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title (English)
                        </label>
                        <input
                          type="text"
                          value={block.content_en}
                          onChange={(e) => updateBlock(block.id, { content_en: e.target.value })}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                          placeholder="CONTRACT"
                        />
                      </div>
                    </>
                  )}

                  {/* Duration block */}
                  {block.block_type === 'duration' && (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        id={`inline-${block.id}`}
                        checked={block.settings?.inline || false}
                        onChange={(e) => {
                          const newSettings = { ...(block.settings || {}), inline: e.target.checked };
                          updateBlock(block.id, { settings: newSettings });
                        }}
                        className="rounded"
                      />
                      <label htmlFor={`inline-${block.id}`} className="text-sm text-gray-700">
                        Display dates in one line (side by side)
                      </label>
                    </div>
                  )}

                  {/* Rate table block */}
                  {block.block_type === 'rate_table' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select rates to display
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`showMainRate-${block.id}`}
                            checked={block.settings?.showMainRate !== false}
                            onChange={(e) => {
                              const newSettings = { ...(block.settings || {}), showMainRate: e.target.checked };
                              updateBlock(block.id, { settings: newSettings });
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`showMainRate-${block.id}`} className="text-sm text-gray-700">
                            Main Rate (hourly/daily/monthly)
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`showTravelRate-${block.id}`}
                            checked={block.settings?.showTravelRate !== false}
                            onChange={(e) => {
                              const newSettings = { ...(block.settings || {}), showTravelRate: e.target.checked };
                              updateBlock(block.id, { settings: newSettings });
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`showTravelRate-${block.id}`} className="text-sm text-gray-700">
                            Travel Rate
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`showTrainingRate-${block.id}`}
                            checked={block.settings?.showTrainingRate !== false}
                            onChange={(e) => {
                              const newSettings = { ...(block.settings || {}), showTrainingRate: e.target.checked };
                              updateBlock(block.id, { settings: newSettings });
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`showTrainingRate-${block.id}`} className="text-sm text-gray-700">
                            Training Rate
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`showCustomRates-${block.id}`}
                            checked={block.settings?.showCustomRates !== false}
                            onChange={(e) => {
                              const newSettings = { ...(block.settings || {}), showCustomRates: e.target.checked };
                              updateBlock(block.id, { settings: newSettings });
                            }}
                            className="rounded"
                          />
                          <label htmlFor={`showCustomRates-${block.id}`} className="text-sm text-gray-700">
                            Custom Rates
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Spacer block */}
                  {block.block_type === 'spacer' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Height (mm)
                      </label>
                      <input
                        type="number"
                        value={block.settings?.height || 20}
                        onChange={(e) => {
                          const newSettings = { ...(block.settings || {}), height: parseInt(e.target.value) || 20 };
                          updateBlock(block.id, { settings: newSettings });
                        }}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        min={1}
                        max={200}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: 20mm</p>
                    </div>
                  )}

                  {/* Signature blocks spacing */}
                  {(block.block_type === 'signatures' || block.block_type === 'signature_lines') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Signature Spacing (pt)
                      </label>
                      <input
                        type="number"
                        value={block.settings?.signatureSpacing || 20}
                        onChange={(e) => {
                          const newSettings = { ...(block.settings || {}), signatureSpacing: parseInt(e.target.value) || 20 };
                          updateBlock(block.id, { settings: newSettings });
                        }}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        min={0}
                        max={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">Space between line and label (default: 20pt)</p>
                    </div>
                  )}

                  {/* Contractor info block */}
                  {block.block_type === 'contractor_info' && (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        id={`representedBy-${block.id}`}
                        checked={block.settings?.showRepresentedBy !== false}
                        onChange={(e) => {
                          const newSettings = { ...(block.settings || {}), showRepresentedBy: e.target.checked };
                          updateBlock(block.id, { settings: newSettings });
                        }}
                        className="rounded"
                      />
                      <label htmlFor={`representedBy-${block.id}`} className="text-sm text-gray-700">
                        Show "Represented by" prefix
                      </label>
                    </div>
                  )}

                  {/* Custom table block */}
                  {block.block_type === 'custom_table' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Table Data
                      </label>
                      <div className="space-y-2">
                        {(block.settings?.rows || [['', '']]).map((row, rowIndex) => (
                          <div key={rowIndex} className="flex gap-1 items-center">
                            {row.map((cell, cellIndex) => (
                              <input
                                key={cellIndex}
                                type="text"
                                value={cell}
                                onChange={(e) => {
                                  const newRows = [...(block.settings?.rows || [['', '']])];
                                  newRows[rowIndex][cellIndex] = e.target.value;
                                  updateBlock(block.id, { settings: { ...block.settings, rows: newRows } });
                                }}
                                className="flex-1 px-2 py-1 border rounded text-xs"
                                placeholder={rowIndex === 0 ? 'Header' : 'Cell'}
                              />
                            ))}
                            <button
                              onClick={() => {
                                const newRows = [...(block.settings?.rows || [['', '']])];
                                newRows.splice(rowIndex, 1);
                                if (newRows.length === 0) newRows.push(['', '']);
                                updateBlock(block.id, { settings: { ...block.settings, rows: newRows } });
                              }}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const rows = block.settings?.rows || [['', '']];
                            const colCount = rows[0]?.length || 2;
                            const newRow = Array(colCount).fill('');
                            updateBlock(block.id, { settings: { ...block.settings, rows: [...rows, newRow] } });
                          }}
                          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                          + Add Row
                        </button>
                        <button
                          onClick={() => {
                            const rows = block.settings?.rows || [['', '']];
                            const newRows = rows.map(row => [...row, '']);
                            updateBlock(block.id, { settings: { ...block.settings, rows: newRows } });
                          }}
                          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                          + Add Column
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Two columns block */}
                  {block.block_type === 'two_columns' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Left Column
                        </label>
                        <textarea
                          value={block.settings?.leftContent || ''}
                          onChange={(e) => {
                            updateBlock(block.id, { settings: { ...block.settings, leftContent: e.target.value } });
                          }}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                          rows={3}
                          placeholder="Left column content..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Right Column
                        </label>
                        <textarea
                          value={block.settings?.rightContent || ''}
                          onChange={(e) => {
                            updateBlock(block.id, { settings: { ...block.settings, rightContent: e.target.value } });
                          }}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                          rows={3}
                          placeholder="Right column content..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Layout options */}
                  <div className="border-b pb-4 mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Layout</h4>

                    <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <input
                        type="checkbox"
                        id={`newRow-${block.id}`}
                        checked={block.settings?.newRow !== false}
                        onChange={(e) => {
                          const newSettings = { ...(block.settings || {}), newRow: e.target.checked };
                          updateBlock(block.id, { settings: newSettings });
                        }}
                        className="rounded"
                      />
                      <label htmlFor={`newRow-${block.id}`} className="text-sm text-gray-700 font-medium">
                        Start new row
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Uncheck to place this block next to the previous one (same line)
                    </p>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Width</label>
                        <select
                          value={block.settings?.width || '100'}
                          onChange={(e) => updateBlock(block.id, {
                            settings: { ...block.settings, width: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="100">Full (100%)</option>
                          <option value="75">3/4 (75%)</option>
                          <option value="66">2/3 (66%)</option>
                          <option value="50">Half (50%)</option>
                          <option value="33">1/3 (33%)</option>
                          <option value="25">1/4 (25%)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Position</label>
                        <select
                          value={block.settings?.position || 'left'}
                          onChange={(e) => updateBlock(block.id, {
                            settings: { ...block.settings, position: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>

                    {/* Visual width/position preview */}
                    <div className="mt-3 p-2 bg-gray-100 rounded">
                      <div className="text-xs text-gray-500 mb-1">Preview</div>
                      <div className="h-3 bg-gray-200 rounded relative">
                        <div
                          className="h-full bg-blue-400 rounded absolute"
                          style={{
                            width: `${block.settings?.width || 100}%`,
                            left: block.settings?.position === 'center'
                              ? `${(100 - (block.settings?.width || 100)) / 2}%`
                              : block.settings?.position === 'right'
                              ? `${100 - (block.settings?.width || 100)}%`
                              : '0%'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Text styling options - hide for spacer, divider, page_break */}
                  {!['spacer', 'divider', 'page_break'].includes(block.block_type) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Text Alignment
                        </label>
                        <select
                          value={block.alignment}
                          onChange={(e) => updateBlock(block.id, { alignment: e.target.value })}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                          <option value="justify">Justify</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Font Size (pt)
                        </label>
                        <input
                          type="number"
                          value={block.font_size}
                          onChange={(e) => updateBlock(block.id, { font_size: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                          min={6}
                          max={72}
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_bold"
                          checked={block.is_bold}
                          onChange={(e) => updateBlock(block.id, { is_bold: e.target.checked })}
                          className="rounded"
                        />
                        <label htmlFor="is_bold" className="text-sm text-gray-700">Bold</label>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Margin Top (mm)</label>
                          <input
                            type="number"
                            value={block.margin_top}
                            onChange={(e) => updateBlock(block.id, { margin_top: parseInt(e.target.value) })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            min={0}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Margin Bottom (mm)</label>
                          <input
                            type="number"
                            value={block.margin_bottom}
                            onChange={(e) => updateBlock(block.id, { margin_bottom: parseInt(e.target.value) })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            min={0}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Live Preview Panel */}
        {showLivePreview && (
          <>
            {/* Resize Handle */}
            <div
              ref={resizeRef}
              onMouseDown={() => setIsResizing(true)}
              className="w-2 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors flex-shrink-0 relative group"
              title="Drag to resize"
            >
              <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-1 bg-gray-400 group-hover:bg-white rounded-full my-2" />
            </div>
            <div
              className="bg-white border-l flex flex-col flex-shrink-0"
              style={{ width: previewPanelWidth }}
            >
              <div className="p-3 border-b flex justify-between items-center">
                <h3 className="font-medium text-gray-900 text-sm">Live Preview</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateLivePreview()}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              <div className="flex-1 p-2 overflow-hidden">
                {livePreviewUrl ? (
                  <iframe
                    src={livePreviewUrl}
                    className="w-full h-full border rounded"
                    title="Live Preview"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    <div className="text-center">
                      <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Loading preview...</p>
                      <p className="text-xs mt-1">Please wait or click Save to refresh</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">PDF Preview</h3>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={`template_preview_${selectedTemplate?.name}.pdf`}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <button
                  onClick={closePreviewModal}
                  className="p-1.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              <iframe
                src={previewUrl}
                className="w-full h-full border rounded"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// New Template Modal
const NewTemplateModal = ({ templateTypes, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'employment',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a template name');
      return;
    }
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">Create New Template</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g., Standard Employment Contract"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Type
            </label>
            <select
              value={formData.template_type}
              onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
            >
              {templateTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
              rows={3}
              placeholder="Optional description..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add custom styles for Quill editor in side panel
const QuillStyles = () => (
  <style>{`
    .rich-text-editor .ql-container {
      min-height: 100px;
      font-size: 13px;
    }
    .rich-text-editor .ql-editor {
      min-height: 100px;
    }
    .rich-text-editor .ql-toolbar {
      padding: 4px;
    }
    .rich-text-editor .ql-toolbar button {
      width: 24px;
      height: 24px;
    }
  `}</style>
);

// Wrapper component to include styles
const ContractTemplateBuilderWithStyles = () => (
  <>
    <QuillStyles />
    <ContractTemplateBuilder />
  </>
);

export default ContractTemplateBuilderWithStyles;
